import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LogIn, LogOut, Car, CalendarClock, AlertCircle, ArrowRight, ArrowRightToLine, ArrowLeftFromLine, Send, BedDouble, X } from "lucide-react";
import BookingPill from "@/components/admin/BookingPill";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { bgForBooking, STATUS_LABEL } from "@/components/admin/bookingStatusColors";

/**
 * Compact "Prossimi 7 giorni" widget for the admin dashboard home.
 * Aggregates check-ins, check-outs, shuttles and upcoming fiscal deadlines.
 *
 * Props:
 *  - setActiveTab(tab): callback to navigate to a specific admin tab
 */
export default function Next7DaysWidget({ setActiveTab, setFilterDate, setFilterDateMode, setFilterStatus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingBriefing, setSendingBriefing] = useState(false);
  // iter189 — tap-preview: mostra un popover con nome/camera/prezzo/status
  // quando l'host tocca una pillola. Chiude tap outside o X.
  const [preview, setPreview] = useState(null);

  // Naviga al tab prenotazioni filtrando per la data del chip. Modalità
  // "stay" (`check_in ≤ date < check_out`) copre sia arrivi che in-house
  // di quel giorno — sempre più utile del filtro "checkin" secco perché
  // include anche gli ospiti già in struttura che restano un'altra
  // notte (segnalazione host: click su "06/07" doveva mostrarmi anche
  // chi arriva ma non solo). Reset dello `status` a "all" così eventuali
  // filtri precedenti non nascondono la prenotazione target.
  const goToBookingsForDate = (dateKey) => {
    setFilterStatus?.("all");
    setFilterDate?.(dateKey);
    setFilterDateMode?.("stay");
    setActiveTab?.("bookings");
  };

  const sendDailyBriefing = async () => {
    setSendingBriefing(true);
    try {
      const res = await api.post("/admin/telegram/daily-briefing-now");
      if (res.data?.ok) {
        toast.success("📨 Briefing inviato su Telegram");
      } else {
        toast.error(`Invio fallito: ${res.data?.reason || "errore sconosciuto"}`);
      }
    } catch (err) {
      toast.error(errMsg(err, "Errore invio briefing"));
    } finally {
      setSendingBriefing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/dashboard/next-7-days")
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5 animate-pulse" data-testid="next7-loading">
        <div className="h-4 w-48 bg-[#F4EFE6] rounded mb-3" />
        <div className="h-3 w-64 bg-[#F4EFE6] rounded" />
      </div>
    );
  }
  if (!data) return null;

  const { check_ins = [], check_outs = [], in_house = [], shuttles = [], deadlines = [], counts = {} } = data;
  const total = (counts.check_ins || 0) + (counts.check_outs || 0) + (counts.shuttles || 0) + (counts.deadlines || 0) + (counts.in_house || 0);
  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);

  // Build a per-day timeline for the next 7 days
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  const eventsForDay = (key) => ({
    checkins: check_ins.filter((c) => c.check_in === key),
    checkouts: check_outs.filter((c) => c.check_out === key),
    in_house: in_house.filter((c) => c.stay_date === key),
    shuttles: shuttles.filter((s) => s.date === key),
    deadlines: deadlines.filter((d) => d.date === key),
  });

  const dayLabel = (key) => {
    const d = new Date(key + "T00:00:00");
    // Split day + month so mobile can wrap the two parts on separate lines
    // (a single "23/08" was overflowing the ~48px column on 390px screens).
    const dayNum = d.toLocaleDateString("it-IT", { day: "2-digit" });
    const monthShort = d.toLocaleDateString("it-IT", { month: "short" }).replace(".", "");
    const dd = `${dayNum}/${d.toLocaleDateString("it-IT", { month: "2-digit" })}`;
    const wd = d.toLocaleDateString("it-IT", { weekday: "short" }).replace(".", "");
    return { wd, dd, dayNum, monthShort, isToday: key === todayKey };
  };

  return (
    <section className="bg-white border border-[#E8E2D9] rounded-xl p-5" data-testid="next7-widget">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-[#AC5D45]" />
          <h2 className="font-display text-xl text-[#1E3A5F]">Prossimi 7 giorni</h2>
          {total > 0 && (
            <span className="text-[10px] uppercase tracking-wide bg-[#1E3A5F] text-white px-2 py-0.5 rounded-full font-bold">
              {total} eventi
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[#635F59]">
          <Pill
            icon={ArrowRightToLine} value={counts.check_ins} label="check-in" tone="emerald"
            title="Vai al tab Check-in per gestire gli arrivi"
            onClick={() => setActiveTab && setActiveTab("checkins")}
          />
          <Pill
            icon={ArrowLeftFromLine} value={counts.check_outs} label="check-out" tone="amber"
            title="Vai a Prenotazioni per gestire i check-out"
            onClick={() => setActiveTab && setActiveTab("bookings")}
          />
          <Pill
            icon={BedDouble} value={counts.in_house} label="in casa" tone="violet"
            title="Vai al tab Check-in — filtro In casa adesso"
            onClick={() => setActiveTab && setActiveTab("checkins")}
          />
          <Pill
            icon={Car} value={counts.shuttles} label="navette" tone="sky"
            title="Vai al tab Richieste Navetta"
            onClick={() => setActiveTab && setActiveTab("shuttle")}
          />
          <Pill
            icon={AlertCircle} value={counts.deadlines} label="scadenze" tone="rose"
            title="Vai al tab Adempimenti per le scadenze fiscali"
            onClick={() => setActiveTab && setActiveTab("deadlines")}
          />
          <button
            onClick={sendDailyBriefing}
            disabled={sendingBriefing}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-[#1E3A5F] hover:bg-[#16304F] text-white font-medium disabled:opacity-50 transition"
            data-testid="send-daily-briefing-btn"
            title="Invia immediatamente su Telegram il riepilogo di oggi (lo stesso che riceverai alle 09:00 automaticamente)"
          >
            <Send className={`w-3 h-3 ${sendingBriefing ? "animate-pulse" : ""}`} />
            {sendingBriefing ? "Invio..." : "Briefing Telegram"}
          </button>
        </div>
      </header>

      {total === 0 ? (
        <p className="text-sm text-[#635F59] py-4 text-center">
          Settimana tranquilla — nessun evento previsto nei prossimi 7 giorni.
        </p>
      ) : (
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((key) => {
            const ev = eventsForDay(key);
            const { wd, dd, dayNum, monthShort, isToday } = dayLabel(key);
            const dayTotal = ev.checkins.length + ev.checkouts.length + ev.in_house.length + ev.shuttles.length + ev.deadlines.length;
            return (
              <div
                key={key}
                className={`rounded-lg border ${isToday ? "border-[#AC5D45] bg-[#FDF8F4]" : "border-[#E8E2D9] bg-[#FDFBF7]"} px-1 py-1.5 sm:p-2 min-h-[120px] flex flex-col`}
                data-testid={`next7-day-${key}`}
              >
                <div className="flex items-baseline justify-between mb-1.5 gap-0.5">
                  <div className="min-w-0 leading-none">
                    <div className={`text-[9px] font-bold tracking-wide uppercase ${isToday ? "text-[#AC5D45]" : "text-[#635F59]"}`}>{wd}</div>
                    {/* Mobile: 2-line compact date so nothing overflows.
                        Desktop: single line dd/MM as before. */}
                    <div className={`sm:hidden font-bold tabular-nums leading-tight ${isToday ? "text-[#AC5D45]" : "text-[#1E3A5F]"}`}>
                      <span className="text-[13px]">{dayNum}</span>{" "}
                      <span className="text-[9px] uppercase opacity-70">{monthShort}</span>
                    </div>
                    <div className={`hidden sm:block text-sm font-bold tabular-nums ${isToday ? "text-[#AC5D45]" : "text-[#1E3A5F]"}`}>{dd}</div>
                  </div>
                  {dayTotal > 0 && (
                    <span className="text-[9px] bg-white border border-[#E8E2D9] rounded-full px-1 sm:px-1.5 font-semibold text-[#635F59] shrink-0">
                      {dayTotal}
                    </span>
                  )}
                </div>

                <div className="space-y-1 overflow-hidden text-[10px] leading-tight">
                  {ev.checkins.map((c) => {
                    const needsRoom = !c.room_name || c.room_name === "Da assegnare";
                    return (
                      <button
                        key={`ci-${c.id}`}
                        onClick={(e) => { e.stopPropagation(); setPreview({ ...c, _kind: "checkin" }); }}
                        className={`w-full text-left ${needsRoom ? "bg-rose-50 hover:bg-rose-100 border-rose-200" : bgForBooking(c) + " hover:brightness-95"} border rounded px-0.5 sm:px-1 py-0.5 sm:py-1`}
                        data-testid={`next7-ci-${c.code}`}
                        title={`Check-in · ${c.guest_name || c.code} · ${c.room_name || ""}`}
                      >
                        <BookingPill booking={c} compact />
                      </button>
                    );
                  })}
                  {ev.checkouts.map((c) => (
                    <button
                      key={`co-${c.id}`}
                      onClick={(e) => { e.stopPropagation(); setPreview({ ...c, _kind: "checkout" }); }}
                      className={`w-full text-left ${bgForBooking(c)} hover:brightness-95 border rounded px-0.5 sm:px-1 py-0.5 sm:py-1`}
                      data-testid={`next7-co-${c.code}`}
                      title={`Check-out · ${c.guest_name || c.code} · ${c.room_name || ""}`}
                    >
                      <BookingPill booking={c} compact />
                    </button>
                  ))}
                  {ev.in_house.map((c) => (
                    <button
                      key={`ih-${c.id}-${c.stay_date}`}
                      onClick={(e) => { e.stopPropagation(); setPreview({ ...c, _kind: "in_house" }); }}
                      className={`w-full text-left ${bgForBooking(c)} hover:brightness-95 border rounded px-0.5 sm:px-1 py-0.5 sm:py-1`}
                      title={`In casa: ${c.guest_name || c.code} · ${c.room_name} · ${c.check_in} → ${c.check_out}`}
                      data-testid={`next7-ih-${c.code}-${c.stay_date}`}
                    >
                      <BookingPill booking={c} compact />
                    </button>
                  ))}
                  {ev.shuttles.map((s, i) => (
                    <button
                      key={`sh-${i}-${s.date}-${s.time}`}
                      onClick={() => setActiveTab && setActiveTab("shuttle")}
                      className="w-full text-left bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded px-1.5 py-1 truncate"
                      title={`Navetta ${s.time}: ${s.origin} → ${s.destination}`}
                      data-testid={`next7-sh-${s.date}-${s.time}`}
                    >
                      <Car className="w-2.5 h-2.5 inline mr-0.5 text-sky-700" />
                      <span className="font-semibold text-sky-900">{s.time}</span>{" "}
                      <span className="text-sky-800">{s.destination?.split(",")[0]}</span>
                    </button>
                  ))}
                  {ev.deadlines.map((d, i) => (
                    <button
                      key={`dl-${i}-${d.label}`}
                      onClick={() => setActiveTab && setActiveTab("deadlines")}
                      className="w-full text-left bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded px-1.5 py-1 truncate"
                      title={d.label}
                      data-testid={`next7-dl-${d.kind}`}
                    >
                      <AlertCircle className="w-2.5 h-2.5 inline mr-0.5 text-rose-700" />
                      <span className="text-rose-900">{d.label.split(" ")[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <footer className="mt-3 pt-3 border-t border-[#E8E2D9] flex flex-wrap gap-3 text-[11px] text-[#635F59]">
        <button onClick={() => setActiveTab && setActiveTab("checkins")} className="hover:text-[#1E3A5F] inline-flex items-center gap-1" data-testid="next7-link-checkins">
          Vai ai Check-in <ArrowRight className="w-3 h-3" />
        </button>
        <button onClick={() => setActiveTab && setActiveTab("calendar")} className="hover:text-[#1E3A5F] inline-flex items-center gap-1" data-testid="next7-link-calendar">
          Apri Calendario <ArrowRight className="w-3 h-3" />
        </button>
        <button onClick={() => setActiveTab && setActiveTab("shuttle")} className="hover:text-[#1E3A5F] inline-flex items-center gap-1" data-testid="next7-link-shuttle">
          Richieste Navetta <ArrowRight className="w-3 h-3" />
        </button>
      </footer>

      {/* iter189 — Popover anteprima al tocco pillola */}
      {preview && (
        <div
          className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-3"
          onClick={() => setPreview(null)}
          data-testid="next7-preview-backdrop"
        >
          <div
            className={`w-full max-w-sm ${bgForBooking(preview)} border-2 rounded-xl p-4 shadow-xl relative`}
            onClick={(e) => e.stopPropagation()}
            data-testid="next7-preview-card"
          >
            <button
              onClick={() => setPreview(null)}
              className="absolute top-2 right-2 text-[#635F59] hover:text-[#1E3A5F]"
              aria-label="Chiudi"
              data-testid="next7-preview-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-[10px] uppercase font-bold text-[#635F59] tracking-wide mb-1">
              {preview._kind === "checkin" ? "Check-in" : preview._kind === "checkout" ? "Check-out" : "In casa"}
              {" · "}{STATUS_LABEL[preview.status] || preview.status || "—"}
            </div>
            <div className="text-lg font-bold text-[#1E3A5F] mb-2">
              {preview.guest_name || `${(preview.guest || {}).first_name || ""} ${(preview.guest || {}).last_name || ""}`.trim() || "(senza nome)"}
            </div>
            <div className="text-sm text-[#1E3A5F] space-y-1">
              <div><b>Camera:</b> {preview.room_name || "Da assegnare"}{preview.guide_room_number != null ? ` · n${preview.guide_room_number}` : ""}</div>
              <div><b>Periodo:</b> {preview.check_in} → {preview.check_out}</div>
              <div><b>Ospiti:</b> {preview.guests || 1}</div>
              {preview.total != null && (
                <div><b>Totale:</b> €{Number(preview.total).toFixed(2)}</div>
              )}
              <div className="text-xs text-[#635F59] font-mono pt-1 border-t border-[#E8E2D9]/60 mt-2">{preview.code}</div>
            </div>
            <button
              className="mt-3 w-full bg-[#AC5D45] hover:bg-[#8f4a37] text-white text-sm font-semibold py-2 rounded-lg transition-colors"
              onClick={() => { setPreview(null); goToBookingsForDate(preview.check_in || preview.stay_date); }}
              data-testid="next7-preview-goto"
            >
              Vai alla prenotazione →
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Pill({ icon: Icon, value, label, tone, onClick, title }) {
  if (!value) return null;
  const tones = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    sky: "bg-sky-100 text-sky-800 border-sky-300",
    rose: "bg-rose-100 text-rose-800 border-rose-300",
    violet: "bg-violet-100 text-violet-800 border-violet-300",
  };
  const hoverTones = {
    emerald: "hover:bg-emerald-200",
    amber: "hover:bg-amber-200",
    sky: "hover:bg-sky-200",
    rose: "hover:bg-rose-200",
    violet: "hover:bg-violet-200",
  };
  const base = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tones[tone]} font-semibold`;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title}
        className={`${base} ${hoverTones[tone]} cursor-pointer transition-colors`}
        data-testid={`next7-pill-${label}`}
      >
        <Icon className="w-3 h-3" />
        {value} {label}
      </button>
    );
  }
  return (
    <span className={base}>
      <Icon className="w-3 h-3" />
      {value} {label}
    </span>
  );
}
